import React, {useState} from "react";
import "./Header.css";
import {useAuth} from "react-oidc-context";
import {
    Callout, DefaultButton, FontWeights,
    IStackStyles,
    mergeStyleSets,
    Persona,
    PersonaPresence,
    PersonaSize,
    Stack,
    StackItem,
    Link,
    Icon
} from "@fluentui/react";
import {useBoolean, useId} from "@fluentui/react-hooks";
import {FnPUser} from "../../model/FnPUser";
import {clearUserInfo, fetchUserPhoto} from "../../services/UserInfoService";
import {LS_USER_PHOTO, ROLE_FUNC_ADMIN} from "../../constants/EiopaFnPConstants";
import strings from "../../loc/strings";
import { useAppSettings } from "../../context/AppSettingsContext";

interface HeaderProps {
    user?: FnPUser
}

function Header(props: HeaderProps) {
    let auth = useAuth();
    const { settings } = useAppSettings();
    const [userPhoto, setUserPhoto] = useState<string | undefined>(undefined);

    React.useEffect(() => {
        const _userPhoto = localStorage.getItem(LS_USER_PHOTO);
        if(_userPhoto === null) {
            if (auth.user) {
                fetchUserPhoto(auth.user.access_token).then((userPhoto) => {
                    if(userPhoto) {
                        const reader = new FileReader();
                        reader.addEventListener('load', () => {
                            localStorage.setItem(LS_USER_PHOTO, reader.result as string);
                        });
                        reader.addEventListener('loadend', () => {
                            setUserPhoto(localStorage.getItem(LS_USER_PHOTO) as string);
                        });
                        reader.readAsDataURL(userPhoto);
                    } else {
                        setUserPhoto(undefined);
                    }
                });
            }
        } else {
            setUserPhoto(_userPhoto);
        }
        return () => {}
    }, []);

    const [isPersonaCalloutVisible, {toggle: toggleIsPersonaCalloutVisible}] = useBoolean(false);

    const buttonId = useId('persona-callout-button');
    const labelId = useId('persona-callout-label');
    const descriptionId = useId('persona-callout-description');

    const headerStackStyles: IStackStyles = {
        root: {
            justifyContent: "space-between",
            alignItems: "center",
            padding: "10px 20px",
            backgroundColor: "#4E7599"
        }
    }

    const styles = mergeStyleSets({
        button: {
            width: 130,
        },
        callout: {
            width: 320,
            maxWidth: '90%',
        },
        title: {
            marginBottom: 12,
            fontWeight: FontWeights.semilight,
        },
        link: {
            display: 'block',
            marginTop: 20,
        },
    });

    function isOnlyFuncAdmin() {
        return props.user?.roles.length === 1 && props.user.roles[0] === ROLE_FUNC_ADMIN;
    }

    function onClickSignOut(): void {
        clearUserInfo();
        auth.signoutRedirect().then(() => console.log(""));
    }

    return (
        <header className="header">
            <Stack styles={headerStackStyles} horizontal>
                <StackItem styles={{root: {color: "white", fontSize: 22, fontWeight: 600}}}>
                    <a href="/" style={{color: "white", textDecoration: "none"}}>
                    {strings.fitnessAndProprietySystem}
                    </a>
                </StackItem>
                <StackItem>
                    <Stack horizontal verticalAlign="center">
                        {settings?.helpManualUrl && (
                            <Link 
                                href={settings.helpManualUrl} 
                                target="_blank" 
                                styles={{ root: { color: 'white !important', textDecoration: 'underline', marginRight: '20px', display: 'flex', alignItems: 'center', fontSize: '14px', selectors: { '&:hover': { color: '#f3f2f1 !important' }, '&:visited': { color: 'white !important' } } } }}
                            >
                                Help <Icon iconName="NavigateExternalInline" styles={{ root: { marginLeft: '5px', fontSize: '14px', color: 'white !important' } }} />
                            </Link>
                        )}
                        <Persona
                            id={buttonId}
                            text={auth.user?.profile.name}
                            size={PersonaSize.size32}
                            presence={PersonaPresence.online}
                            hidePersonaDetails={true}
                            imageAlt={auth.user?.profile.name}
                            imageUrl={userPhoto}
                            onClick={toggleIsPersonaCalloutVisible}
                        />
                        {isPersonaCalloutVisible && (
                            <Callout
                                className={styles.callout}
                                ariaLabelledBy={labelId}
                                ariaDescribedBy={descriptionId}
                                role="dialog"
                                gapSpace={0}
                                target={`#${buttonId}`}
                                onDismiss={toggleIsPersonaCalloutVisible}
                            >
                                <div className="CalloutContainer">
                                    <Persona
                                        id={buttonId}
                                        text={auth.user?.profile.name}
                                        size={PersonaSize.size72}
                                        presence={PersonaPresence.online}
                                        hidePersonaDetails={true}
                                        imageAlt={auth.user?.profile.name}
                                        imageUrl={userPhoto}
                                    />
                                    <div className="CalloutUserDetails">
                                        <div className="CalloutUserName">
                                            {auth.user?.profile.name}
                                        </div>
                                        {props.user &&
                                            <>
                                                <div className="CalloutUserRoles">
                                                    {props.user.roles.map((role) =>  {
                                                        let roleArr = role.split(" ");
                                                        roleArr.splice(0, 2);
                                                        let roleName = roleArr.join(" ");
                                                        return <div>{roleName}</div>
                                                    })}
                                                </div>
                                                {isOnlyFuncAdmin() ? <></> :
                                                    <span className="CalloutUserCA">
                                                    {props.user.ncaCode}
                                                    </span>
                                                }
                                            </>

                                        }
                                    </div>
                                </div>
                                <div className="CalloutSignOutButton">
                                    <DefaultButton
                                        styles={{root: {border: "none"}}}
                                        text="Sign out"
                                        onClick={onClickSignOut}
                                    />
                                </div>
                            </Callout>
                        )}
                    </Stack>
                </StackItem>
            </Stack>
        </header>
    );
}

export default Header;
